import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loading } from '@/components/OpsUI';
import { STATUS } from './campaignUtils';
import { ImportCsvButton } from './ImportCsvButton';
import { AddContactDialog } from './AddContactDialog';
import { SendWelcomeButton } from './SendWelcomeButton';
import { ContactRow } from './ContactRow';

export function ContactsPanel({ campaign, canAct, onChange }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [selected, setSelected] = useState([]);

  const load = useCallback(() => {
    api.get(`/campaigns/${campaign.id}/contacts`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [campaign.id]);
  const refresh = useCallback(() => { load(); onChange(); }, [load, onChange]);
  useEffect(() => { load(); }, [load]);

  const sending = rows?.some((r) => r.email_status === 'queued' || r.wa_status === 'sending');
  useEffect(() => {
    if (!sending) return undefined;
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, [sending, refresh]);

  const shown = useMemo(() => (rows || []).filter((r) => {
    const text = `${r.pastor} ${r.church} ${r.email} ${r.phone}`.toLowerCase();
    return (status === 'all' || r.status === status) && text.includes(q.toLowerCase());
  }), [rows, q, status]);
  const allSel = shown.length > 0 && shown.every((r) => selected.includes(r.id));
  const toggleAll = () => setSelected(allSel ? [] : shown.map((r) => r.id));
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const pending = (rows || []).filter((r) => r.email && !r.unsubscribed && ['none', 'failed'].includes(r.email_status)).length;
  const pendingWa = (rows || []).filter((r) => r.phone && !r.unsubscribed && ['none', 'failed', 'undelivered', undefined].includes(r.wa_status)).length;
  const done = () => { setSelected([]); refresh(); };

  return (
    <div className="space-y-4" data-testid="campaign-contacts-panel">
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Buscar pastor, iglesia, correo..." value={q} onChange={(e) => setQ(e.target.value)} className="w-64" data-testid="contacts-search" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-52" data-testid="contacts-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        {canAct && (
          <div className="ml-auto flex flex-wrap gap-2">
            <ImportCsvButton campaignId={campaign.id} onDone={refresh} />
            <AddContactDialog campaignId={campaign.id} onDone={refresh} />
            <SendWelcomeButton campaign={campaign} selected={selected} pending={pendingWa} onDone={done} channel="whatsapp" />
            <SendWelcomeButton campaign={campaign} selected={selected} pending={pending} onDone={done} channel="email" />
          </div>
        )}
      </div>
      {sending && <div className="text-xs text-amber-300" data-testid="contacts-sending-indicator">Enviando mensajes… se actualiza solo.</div>}
      {rows === null ? <Loading /> : (
        <Card className="overflow-x-auto">
          <Table data-testid="contacts-table">
            <TableHeader>
              <TableRow>
                {canAct && <TableHead className="w-8"><Checkbox checked={allSel} onCheckedChange={toggleAll} data-testid="contacts-select-all" /></TableHead>}
                <TableHead>Pastor / Iglesia</TableHead>
                <TableHead>Correo</TableHead>
                <TableHead>WhatsApp</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Correo de bienvenida</TableHead>
                {canAct && <TableHead className="text-right">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((r) => (
                <ContactRow key={r.id} r={r} campaignId={campaign.id} canAct={canAct}
                  checked={selected.includes(r.id)} onToggle={() => toggle(r.id)} onChange={refresh} />
              ))}
            </TableBody>
          </Table>
          {shown.length === 0 && (
            <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]" data-testid="contacts-empty">
              {rows.length ? 'Sin resultados para este filtro.' : 'Aún no hay congregaciones. Carga el CSV del evento.'}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
